import React, { useEffect, useState } from "react"
import { Autocomplete, Box, TextField } from "@mui/material"
import { SelectSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import { AppFilterChip, AppFormGrid, AppFormSection, AppPillToggle } from "@components"
import { LIMITS, atLimit, isAcrissCode, setSitesMode, sitesMode } from "./@services/profile"
import { Cell, FieldLabel, Helper } from "./fields"


const SITES_MODES = [{ value: 'sites', label: 'Sites' }, { value: 'kind', label: 'Type' }, { value: 'group', label: 'Site group' }]
const fieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: dsColorNew.white } }
const chipsSx = { display: 'flex', flexWrap: 'wrap', gap: 0.75 }
const toggle = (items, item) => (items.includes(item) ? items.filter(x => x !== item) : [...items, item])

/** "What you want her to watch": offices, rental durations, car groups and sites — every option from `options`. */
export default function FocusSection({ value, options, onPatch, onChange, disabled }) {
	const focus = value.focus
	const channels = focus.channels
	// the picking mode is the screen's: switching to an empty one must not snap back (criterion 6)
	// (a Discard or a save brings the saved choice back)
	const [mode, setMode] = useState(() => sitesMode(channels))
	const derived = sitesMode(channels)
	const picked = !!(channels.site_ids.length || channels.kind || channels.group)
	useEffect(() => { if (picked) setMode(derived) }, [derived, picked])

	const offices = options?.offices || []
	const lors = options?.lors || []
	const classes = options?.classes || []
	const sites = options?.sites || []
	const siteName = new Map(sites.map(s => [s.id, s.name]))
	const officesFull = atLimit(focus.offices, LIMITS.offices)
	const lorsFull = atLimit(focus.lors, LIMITS.lors)
	const codesFull = atLimit(focus.acriss_list, LIMITS.codes)
	const sitesFull = atLimit(channels.site_ids, LIMITS.sites)
	const kindCounts = (options?.site_kinds || []).filter(k => k.count).map(k => `${k.label} ${k.count}`).join(' · ')

	const patchFocus = (next) => onPatch({ focus: next })
	const patchChannels = (next) => onPatch({ focus: { channels: next } })

	return <AppFormSection icon="eye" title="What you want her to watch" hint="Her answers lean on this. Leave a field empty and she treats your whole portfolio as the scope.">
		<AppFormGrid>
			<Cell span={6}>
				<FieldLabel count={focus.offices.length} max={LIMITS.offices}>Offices</FieldLabel>
				<Autocomplete
					multiple size="small" disabled={disabled}
					options={offices}
					value={focus.offices}
					getOptionLabel={o => o?.label ?? ''}
					isOptionEqualToValue={(o, v) => o.id === v.id}
					getOptionDisabled={o => officesFull && !focus.offices.some(v => v.id === o.id)}
					onChange={(_, next) => patchFocus({ offices: next.slice(0, LIMITS.offices).map(o => ({ id: o.id, label: o.label })) })}
					renderInput={params => <TextField {...params} placeholder={focus.offices.length ? '' : 'All offices'} inputProps={{ ...params.inputProps, 'aria-label': 'Offices' }}/>}
					sx={fieldSx}
				/>
				<Helper>Only the offices your account can read. At {LIMITS.offices} the list stops taking more.</Helper>
			</Cell>

			<Cell span={6}>
				<FieldLabel count={focus.lors.length} max={LIMITS.lors}>Rental durations</FieldLabel>
				<Box sx={chipsSx}>
					{lors.map(d => {
						const on = focus.lors.includes(d)
						return <AppFilterChip
							key={d} label={`${d} day${d === 1 ? '' : 's'}`} selected={on}
							disabled={disabled || (!on && lorsFull)}
							onClick={() => patchFocus({ lors: toggle(focus.lors, d).sort((a, b) => a - b) })}
						/>
					})}
				</Box>
				<Helper>The durations your searches use. Empty = all of them.</Helper>
			</Cell>

			<Cell span={6}>
				<FieldLabel note={`${focus.acriss_classes.length} class${focus.acriss_classes.length === 1 ? '' : 'es'} · ${focus.acriss_list.length}/${LIMITS.codes} codes`}>Car groups (ACRISS)</FieldLabel>
				<Box sx={chipsSx}>
					{classes.map(c => <AppFilterChip
						key={c.key}
						label={<>{c.label} <Box component="span" sx={{ fontFamily: 'Roboto Mono, monospace', fontSize: 11, color: dsColorNew.textMuted }}>{c.key}</Box></>}
						selected={focus.acriss_classes.includes(c.key)}
						disabled={disabled}
						onClick={() => patchFocus({ acriss_classes: toggle(focus.acriss_classes, c.key) })}
					/>)}
				</Box>
				<Autocomplete
					multiple freeSolo size="small" disabled={disabled}
					options={options?.acriss || []}
					value={focus.acriss_list}
					getOptionDisabled={() => codesFull}
					onChange={(_, next) => {
						const codes = [...new Set(next.map(isAcrissCode).filter(Boolean))].slice(0, LIMITS.codes)
						patchFocus({ acriss_list: codes })
					}}
					renderInput={params => <TextField {...params} placeholder={focus.acriss_list.length ? 'Add a code…' : 'Add a code — EDMR, CDMR…'} inputProps={{ ...params.inputProps, 'aria-label': 'ACRISS codes' }}/>}
					sx={{ ...fieldSx, mt: 1.25, '& .MuiChip-label': { fontFamily: 'Roboto Mono, monospace' } }}
				/>
				<Helper>{(options?.acriss || []).length
					? 'A whole class, or single codes from your fleet.'
					: 'A whole class, or single codes. No suggestions yet for your fleet — type a 4-letter code.'}</Helper>
			</Cell>

			<Cell span={6}>
				<FieldLabel {...(mode === 'sites' && { count: channels.site_ids.length, max: LIMITS.sites })}>Sites</FieldLabel>
				<AppPillToggle
					size="small" aria-label="Pick sites by"
					value={mode} options={SITES_MODES}
					onChange={m => { if (disabled) return; setMode(m); onChange(setSitesMode(value, m)) }}
					sx={{ mb: 1 }}
				/>
				{mode === 'sites' && <Autocomplete
					multiple size="small" disabled={disabled}
					options={sites.map(s => s.id)}
					value={channels.site_ids}
					getOptionLabel={id => siteName.get(id) || 'Unknown site'}
					getOptionDisabled={id => sitesFull && !channels.site_ids.includes(id)}
					onChange={(_, next) => patchChannels({ site_ids: next.slice(0, LIMITS.sites), kind: null, group: null })}
					renderInput={params => <TextField {...params} placeholder={channels.site_ids.length ? '' : 'All sites'} inputProps={{ ...params.inputProps, 'aria-label': 'Sites' }}/>}
					sx={fieldSx}
				/>}
				{mode === 'kind' && <SelectSeeker
					label="Site type" placeholder="Any type" disabled={disabled}
					value={channels.kind || ''}
					options={(options?.site_kinds || []).map(k => ({ value: k.id, label: `${k.label} (${k.count})` }))}
					onChange={e => patchChannels({ kind: e.target.value || null, site_ids: [], group: null })}
				/>}
				{mode === 'group' && <SelectSeeker
					label="Site group" placeholder={(options?.site_groups || []).length ? 'Any group' : 'No site groups yet'} disabled={disabled}
					value={channels.group || ''}
					options={(options?.site_groups || []).map(g => ({ value: g, label: g }))}
					onChange={e => patchChannels({ group: e.target.value || null, site_ids: [], kind: null })}
				/>}
				<Helper>One of the three: sites you name, <b>a type</b>{kindCounts ? ` (${kindCounts})` : ''} or <b>a site group</b> of yours. Switching clears the other two.</Helper>
			</Cell>
		</AppFormGrid>
	</AppFormSection>
}
